import axios from 'axios';
import { isPlatformApiCrossOrigin, resolvePlatformApiBaseUrl } from '../utils/platformApiBaseUrl';
import { sanitizeReviewMarketplaceIds } from '../utils/reviewPlatformFilter';
import { REVIEW_AI_GROUPS, storeUserGroupsAllow } from '../utils/storeUserGroups';

// Scraper API (local backend during dev, or relative paths on collector.*)
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) || '';

// Store / importer / obp_ai — always api.brandtrax.* when UI is on collector.*
const PLATFORM_API_BASE_URL = resolvePlatformApiBaseUrl();
// Cross-origin store calls use Bearer only; cookies + withCredentials break CORS on api.*
const PLATFORM_API_USE_CREDENTIALS = !isPlatformApiCrossOrigin();

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store service client (uses shared base URL, with credentials for cookie-based auth)
export const storeClient = axios.create({
  baseURL: PLATFORM_API_BASE_URL,
  timeout: 30000,
  withCredentials: PLATFORM_API_USE_CREDENTIALS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Importer service client (uses shared base URL, with credentials for cookie-based auth)
export const importerClient = axios.create({
  baseURL: PLATFORM_API_BASE_URL,
  timeout: 30000,
  withCredentials: PLATFORM_API_USE_CREDENTIALS,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshStoreBearerPromise: Promise<string | null> | null = null;

async function getStoreBearerForRequest(): Promise<string | null> {
  let token = window.localStorage.getItem('storeBearer');
  if (!token || shouldRefreshStoreBearer()) {
    token = await refreshStoreBearer();
  }
  return token;
}

function attachBearerHeader(config: { headers?: unknown }, token: string) {
  const headers = (config.headers || {}) as Record<string, string>;
  headers.Authorization = `Bearer ${token}`;
  config.headers = headers;
}

// Attach Authorization: Bearer from Store user token on every Store API call.
// Skip /users/login — that endpoint uses x-authorization instead.
storeClient.interceptors.request.use(async config => {
  const url: string = (config.url as string) || '';
  if (url.includes('/users/login')) {
    return config;
  }
  const token = await getStoreBearerForRequest();
  if (token) {
    attachBearerHeader(config, token);
  }
  return config;
});

// Importer routes through the same API gateway and expects the Store user JWT.
importerClient.interceptors.request.use(async config => {
  const token = await getStoreBearerForRequest();
  if (token) {
    attachBearerHeader(config, token);
  }
  return config;
});

// Helper to refresh store bearer by exchanging Azure cookie for Store token
function isJwtExpired(token: string): boolean {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return true;
    const json = JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/')));
    if (!json || typeof json.exp !== 'number') return false;
    const nowSec = Math.floor(Date.now() / 1000);
    return json.exp <= nowSec + 60; // consider expiring within 60s as expired
  } catch {
    return false;
  }
}

function shouldRefreshStoreBearer(): boolean {
  const bearer = window.localStorage.getItem('storeBearer');
  const tsStr = window.localStorage.getItem('storeBearerTs');
  if (!bearer) return true;
  // Refresh if token appears expired based on exp
  if (isJwtExpired(bearer)) return true;
  // Or if token is older than 10 minutes (keep fresh for user switches)
  const ts = tsStr ? parseInt(tsStr, 10) : 0;
  const ageMs = Date.now() - ts;
  return !ts || ageMs > 10 * 60 * 1000;
}

async function refreshStoreBearer(): Promise<string | null> {
  if (refreshStoreBearerPromise) {
    return refreshStoreBearerPromise;
  }

  refreshStoreBearerPromise = refreshStoreBearerOnce().finally(() => {
    refreshStoreBearerPromise = null;
  });
  return refreshStoreBearerPromise;
}

async function refreshStoreBearerOnce(): Promise<string | null> {
  try {
    let current: any;

    // For local development, use the provided token directly
    if (import.meta.env.VITE_USER_TOKEN) {
      const token = import.meta.env.VITE_USER_TOKEN;
      const payload = JSON.parse(atob(token.split('.')[1]));
      current = {
        data: `${token}:${payload.sub}:${Date.now()}`, // Format: token:providerId:nonce
      };
    } else {
      // Production/deployed environment - get token from scraper backend
      current = await apiClient.get(endpoints.currentUserToken);
    }

    const parts = String(current.data).split(':');
    if (parts.length >= 2) {
      const xAuth = parts[0];
      const providerId = parts[1];
      const xNonce = parts[2] || '';
      const res = await storeClient.get(endpoints.storeLogin(providerId), {
        headers: { 'x-authorization': xAuth, 'x-nonce': xNonce },
      });
      const bearer = res?.data?.auth as string | undefined;
      const userGroups = res?.data?.groups as string[] | undefined;
      const storeProviderId = res?.data?.providerId as string | undefined;
      if (bearer) {
        window.localStorage.setItem('storeBearer', bearer);
        window.localStorage.setItem('storeBearerTs', String(Date.now()));
        if (storeProviderId) {
          window.localStorage.setItem('storeProviderId', storeProviderId);
        }
        try {
          const payload = JSON.parse(
            atob(xAuth.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
          );
          if (payload?.sub) {
            window.localStorage.setItem('azureJwtSub', String(payload.sub));
          }
        } catch {
          // optional legacy sub mapping for display
        }
        // Store user groups for role-based access checks (e.g., GOD users can see all brands)
        if (userGroups && Array.isArray(userGroups)) {
          window.localStorage.setItem('storeUserGroups', JSON.stringify(userGroups));
        }
        return bearer;
      }
    }
  } catch (e) {
    // no-op
  }
  return null;
}

function readStoreUserGroups(): string[] {
  try {
    const groupsStr = window.localStorage.getItem('storeUserGroups');
    if (!groupsStr) return [];
    const groups = JSON.parse(groupsStr);
    return Array.isArray(groups) ? groups : [];
  } catch {
    return [];
  }
}

// Helper to check if current user has GOD role (group may be "GOD" or "god" from backend)
export function isGodUser(): boolean {
  return storeUserGroupsAllow(readStoreUserGroups(), ['god']);
}

/** Reviews inject-gate and Auto-process: manager, admin, or god. */
export function canUseReviewAi(): boolean {
  return storeUserGroupsAllow(readStoreUserGroups(), REVIEW_AI_GROUPS);
}

// On 401 from Store, attempt one silent refresh and retry once
storeClient.interceptors.response.use(
  r => r,
  async error => {
    const original = error.config || {};
    const url: string = (original.url as string) || '';
    // Avoid infinite loop: if 401 originates from the Store login endpoint itself, do not retry/refresh
    if (url.includes('/users/login')) {
      return Promise.reject(error);
    }
    if (error?.response?.status === 401 && !original._retry) {
      (original as any)._retry = true;
      const bearer = await refreshStoreBearer();
      if (bearer) {
        original.headers = original.headers || {};
        (original.headers as any).Authorization = `Bearer ${bearer}`;
        return storeClient(original);
      }
      // Could not obtain a store JWT — session is gone; send user to login.
      try {
        const path = window.location.pathname || '';
        if (path !== '/login') {
          window.location.href = '/login';
        }
      } catch (_) {
        // ignore
      }
    }
    // 401 after a successful refresh/retry means authorization failed (e.g. brand access), not auth.
    return Promise.reject(error);
  }
);

// On 401 from Importer, attempt one silent refresh and retry once (same as Store)
importerClient.interceptors.response.use(
  r => r,
  async error => {
    const original = error.config || {};
    if (error?.response?.status === 401 && !original._retry) {
      (original as any)._retry = true;
      const bearer = await refreshStoreBearer();
      if (bearer) {
        original.headers = original.headers || {};
        (original.headers as any).Authorization = `Bearer ${bearer}`;
        return importerClient(original);
      }
    }
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error?.response?.status === 401) {
      const path = window.location.pathname || '';
      // Avoid redirect loops on login or auth callback routes
      if (path !== '/login' && !path.startsWith('/auth/azure')) {
        window.location.href = '/login';
      }
    }
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// API endpoints
export const endpoints = {
  // System status
  systemStatus: '/api/v1/scraper/status',
  extensionWorkers: '/api/v1/scraper/extension-work/workers',

  // Marketplace definitions
  definitions: '/api/v1/scraper/definitions/',
  definitionTemplates: '/api/v1/scraper/definitions/templates',
  definition: (id: string) => `/api/v1/scraper/definitions/${id}`,
  definitionResolved: (id: string) => `/api/v1/scraper/definitions/${id}/resolved`,
  definitionChildren: (id: string) => `/api/v1/scraper/definitions/${id}/children`,
  definitionWorkStatus: (id: string) => `/api/v1/scraper/definitions/${id}/work-status`,
  definitionVersions: (id: string, limit: number = 50) =>
    `/api/v1/scraper/definitions/${id}/versions?limit=${limit}`,
  definitionRollback: (id: string, versionId: string) =>
    `/api/v1/scraper/definitions/${id}/rollback/${versionId}`,

  // Scraping tasks
  tasks: '/api/v1/scraper/tasks/',
  deletePendingTasks: '/api/v1/scraper/tasks/pending',
  task: (id: string) => `/api/v1/scraper/tasks/${id}`,
  taskResults: (id: string) => `/api/v1/scraper/tasks/${id}/results`,

  // Task groups
  taskGroups: '/api/v1/scraper/task-groups/',
  taskGroup: (id: string) => `/api/v1/scraper/task-groups/${id}`,
  taskGroupSuggest: '/api/v1/scraper/task-groups/suggest',

  // Definition test suites (one keyword across every marketplace)
  definitionTestSuites: '/api/v1/scraper/definition-test-suites/',
  definitionTestSuite: (id: string) => `/api/v1/scraper/definition-test-suites/${id}`,
  definitionTestSuiteTrend: '/api/v1/scraper/definition-test-suites/trend',
  // Reviews
  reviews: (
    brand?: string,
    stage?: string,
    limit: number = 50,
    offset: number = 0,
    q?: string,
    marketplaceIds?: string[],
    product?: string
  ) => {
    const params = new URLSearchParams();
    if (brand) params.append('brand', brand);
    if (stage) params.append('stage', stage);
    if (q && q.trim()) params.append('q', q.trim());
    if (product && product.trim()) params.append('product', product.trim());
    sanitizeReviewMarketplaceIds(marketplaceIds).forEach(id => params.append('marketplace_id', id));
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    return `/api/v1/scraper/tasks/reviews?${params.toString()}`;
  },
  reviewsBackfillInjectGate: (brand: string, maxListings: number = 20000) => {
    const params = new URLSearchParams();
    params.append('brand', brand);
    params.append('max_listings', String(maxListings));
    return `/api/v1/scraper/tasks/reviews/backfill-inject-gate?${params.toString()}`;
  },
  reviewsAutoProcessLock: (brand: string, owner?: string, stage?: string) => {
    const params = new URLSearchParams();
    params.append('brand', brand);
    if (owner) params.append('owner', owner);
    if (stage) params.append('stage', stage);
    return `/api/v1/scraper/tasks/reviews/auto-process-lock?${params.toString()}`;
  },
  reviewsAutoProcessLockHeartbeat: (brand: string, owner: string) => {
    const params = new URLSearchParams();
    params.append('brand', brand);
    params.append('owner', owner);
    return `/api/v1/scraper/tasks/reviews/auto-process-lock/heartbeat?${params.toString()}`;
  },
  reviewsAutoProcessLockRelease: (brand: string, owner: string) => {
    const params = new URLSearchParams();
    params.append('brand', brand);
    params.append('owner', owner);
    return `/api/v1/scraper/tasks/reviews/auto-process-lock/release?${params.toString()}`;
  },

  // Test results (if exposed)
  testResults: '/api/v1/scraper/test-results',
  testResult: (id: string) => `/api/v1/scraper/test-results/${id}`,

  // Validation (moved under scraper prefix)
  validate: '/api/v1/scraper/ai',
  validateMarketplace: (marketplace: string) => `/api/v1/scraper/ai/${marketplace}`,

  // Statistics
  statistics: '/api/v1/scraper/statistics',
  marketplaceStats: (marketplace: string) => `/api/v1/scraper/statistics/${marketplace}`,

  // Store service (catalog)
  // Use the user-scoped endpoint as in dashboard to avoid 405s on GET
  storeBrands: () => `/api/v1/store/brands/user`,
  storeBrandInfringementOptions: (brandName: string) =>
    `/api/v1/store/brands/names/${encodeURIComponent(brandName)}/infringement-options`,
  // All brand names (for GOD users) - BrandController /names, returns List<String>
  storeAllBrandNames: () => `/api/v1/store/brands/names`,
  // Fetch products by brand NAME (names are unique in Store)
  storeProductsByBrandName: (brandName: string, page: number = 0, size: number = 100) =>
    `/api/v1/store/products/brand/${encodeURIComponent(brandName)}?page=${page}&size=${size}`,
  storeLogin: (providerId: string) => `/api/v1/store/users/login/${providerId}`,
  storeUserByProvider: (providerId: string) =>
    `/api/v1/store/users/provider/${encodeURIComponent(providerId)}`,
  storeUserNames: '/api/v1/store/users/names',
  currentUserToken: `/api/v1/users/current`,

  // Store service (listings)
  storeCurrencies: '/api/v1/store/listings/currencies',
  storeCountries: '/api/v1/store/dashboard/all_countries',
  storeListings: '/api/v1/store/listings',
  storeListing: (id: string) => `/api/v1/store/listings/${id}`,

  // Store service (sellers)
  storeSellers: '/api/v1/store/sellers',
  storeSeller: (id: string) => `/api/v1/store/sellers/${id}`,

  // Importer service (scraper listings)
  importScraperListing: '/api/v1/importer/listings/scraper',
  importRaceListingSummaries: '/api/v1/importer/race/listings/summaries',

  // Issues (scraper issue reports)
  issues: '/api/v1/scraper/issues/',
  issue: (id: string) => `/api/v1/scraper/issues/${id}`,
  issueUpdateState: (id: string) => `/api/v1/scraper/issues/${id}/state`,

  // Admin (on-demand env sync; target DB URL in request body only)
  adminSyncDefinitions: '/api/v1/scraper/admin/sync-definitions',
};

export const storeAuth = {
  shouldRefreshStoreBearer,
  refreshStoreBearer,
  getStoreBearerForRequest,
};

export default apiClient;
